import { Journey } from '@audibene/ta.utils-opportunity'
import { Button, Stack, Typography } from '@mui/material'
import { SelectChangeEvent } from '@mui/material/Select'
import { format, isAfter, setHours, setMinutes, startOfDay } from 'date-fns'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Navigate, useNavigate, useParams } from 'react-router-dom'
import { Country, AppointmentType, Route, RouteParameters } from '../../../constants'
import { useIsSchedulingV2RedirectPending } from '../../../hooks'
import { useAppointmentTypeOptions } from '../../../hooks/useAppointmentTypeOptions'
import { useAppointments } from '../../../hooks/useAppointments'
import { useAppointmentResources } from 'hooks/useAppointmentResources'
import { useCalendarIntegrationAppointmentTypeMappings } from '../../../hooks/useCalendarIntegrationAppointmentTypeMappings'
import { useCreateAppointment } from '../../../hooks/useCreateAppointment'
import { useCustomerByGid } from '../../../hooks/useCustomerByGid'
import { useOpportunities } from '../../../hooks/services/opportunities'
import { useShopBySalesforceId } from '../../../hooks/useShopBySalesforceId'
import { AppointmentCreateRequest, AppointmentsGetManyRequest } from '../../../types'
import { hydratePathname } from '../../../util'
import { isCalendarIntegration } from 'util/calendar-integrations'
import { AppointmentForm } from '../components/AppointmentForm'
import { CreateAppointmentNewSkeleton } from './sceleton'
import { StyledContainer, StyledTitle, StyledActionButtonsContainer } from './styles'

type AppointmentData = {
  appointmentType?: string
  dateTime?: Date
}

export interface ScheduledAppointment {
  id: string
  appointmentType: string
  date: string
  time: string
}

interface AppointmentTypeConfig {
  duration_in_minutes: number
}

interface CreateAppointmentNewProps {
  customerGid?: string
  countryCode?: string
}

const toDateSelectorFormat = (date: Date): string => {
  return format(date, 'yyyy-MM-dd')
}

const CreateAppointmentNew = ({ customerGid, countryCode }: CreateAppointmentNewProps = {}) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const params = useParams()
  const isV2RedirectPending = useIsSchedulingV2RedirectPending()

  const finalCustomerGid = customerGid ?? params[RouteParameters.CUSTOMER_GID]
  const finalCountryCode = countryCode ?? params[RouteParameters.COUNTRY]

  const [appointmentData, setAppointmentData] = useState<AppointmentData>({})
  const [error, setError] = useState<Error | null>(null)

  const successPath =
    finalCustomerGid &&
    finalCountryCode &&
    hydratePathname(Route.CREATE_APPOINTMENT_SUCCESS, {
      [RouteParameters.COUNTRY]: finalCountryCode?.toLowerCase() || '',
      [RouteParameters.CUSTOMER_GID]: finalCustomerGid,
    })

  const createAppointmentMutation = useCreateAppointment()

  const { data: opportunities, isLoading: isOpportunityLoading } = useOpportunities(
    { customerGid: finalCustomerGid, countryCode: finalCountryCode as Country, isCurrent: true, limit: 1 },
    { enabled: Boolean(finalCustomerGid && finalCountryCode) }
  )
  const opportunity = opportunities?.[0]
  const journey = opportunity?.journey as Journey | undefined
  const opportunityGid = opportunity?.salesforceId

  const { data: customerData, isLoading: isCustomerLoading } = useCustomerByGid(finalCustomerGid)

  const shopSalesforceId = opportunity?.partnerShopSalesforceId
  const { data: shopsData, isLoading: isShopLoading } = useShopBySalesforceId(shopSalesforceId)

  const shopId = shopsData?.id
  const { data: calendarMappings, isLoading: isCalendarMappingsLoading } = useCalendarIntegrationAppointmentTypeMappings(
    { shop_id: shopId, delivery_model: opportunity?.deliveryModel },
    Boolean(shopId)
  )
  const hasIntegratedCalendar = calendarMappings?.data.some((mapping) => isCalendarIntegration(mapping.calendar_integration_type))
  const { data: resources, isLoading: isResourcesLoading } = useAppointmentResources(
    { gid: opportunity?.fitterSalesforceId },
    { enabled: hasIntegratedCalendar && !!opportunity?.fitterSalesforceId }
  )
  const resource = resources?.data?.[0]

  const isIntegratedCalendarDataLoading = Boolean(shopSalesforceId) && (isShopLoading || isCalendarMappingsLoading)

  const appointmentsRequestForFirstAppointmentType: AppointmentsGetManyRequest = {
    country: finalCountryCode as Country,
    journey: journey ? [journey] : undefined,
    opportunity_gid: opportunityGid ? [opportunityGid] : undefined,
  }

  const { appointmentTypes: appointmentTypesConfig, isLoading: isAppointmentTypesLoading } = useAppointmentTypeOptions(
    {
      country: (finalCountryCode as Country) ?? Country.DE,
      journey: (journey as Journey) ?? (Journey as any).TELEAUDIOLOGY_HEAR_TODAY,
    },
    { enabled: Boolean(finalCountryCode && journey) }
  )

  const { data: appointments, isLoading: isAppointmentsLoading } = useAppointments(
    appointmentsRequestForFirstAppointmentType,
    Boolean(opportunityGid && finalCountryCode && journey)
  )

  const availableAppointmentTypes = !appointmentTypesConfig
    ? []
    : Object.entries(appointmentTypesConfig)
        .filter(([appointmentType]) => {
          return !(appointments?.length && appointmentType === AppointmentType.HEARING_AID_EVALUATION)
        })
        .map(([appointmentType, config]) => ({
          type: appointmentType as AppointmentType,
          config: config as AppointmentTypeConfig,
        }))

  const getAppointmentConfig = (appointmentType: string): AppointmentTypeConfig | null => {
    const found = availableAppointmentTypes.find((item) => item.type === appointmentType)
    return found?.config || null
  }

  const handleAppointmentTypeChange = (event: SelectChangeEvent) => {
    setAppointmentData((prev) => ({
      ...prev,
      appointmentType: event.target.value,
    }))
  }

  const handleDateChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newDate = event.target.value
    if (!newDate) {
      setAppointmentData((prev) => ({ ...prev, dateTime: undefined }))
      return
    }

    const parsedDate = new Date(newDate)
    setAppointmentData((prev) => {
      if (!prev.dateTime) {
        return { ...prev, dateTime: startOfDay(parsedDate) }
      }

      const hours = prev.dateTime.getHours()
      const minutes = prev.dateTime.getMinutes()
      const newDateTime = setMinutes(setHours(parsedDate, hours), minutes)

      const now = new Date()
      if (!isAfter(newDateTime, now)) {
        return { ...prev, dateTime: startOfDay(parsedDate) }
      }

      return { ...prev, dateTime: newDateTime }
    })
  }

  const handleTimeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newTime = event.target.value
    if (!newTime) {
      setAppointmentData((prev) => ({
        ...prev,
        dateTime: prev.dateTime ? startOfDay(prev.dateTime) : undefined,
      }))
      return
    }

    const [hours, minutes] = newTime.split(':').map(Number)
    setAppointmentData((prev) => {
      const baseDate = prev.dateTime || new Date()
      const newDateTime = setMinutes(setHours(baseDate, hours), minutes)
      return { ...prev, dateTime: newDateTime }
    })
  }

  const getTodayDate = () => {
    return toDateSelectorFormat(new Date())
  }

  const getCurrentTime = () => {
    const now = new Date()
    return format(now, 'HH:mm')
  }

  const getMinTime = () => {
    if (!appointmentData.dateTime) return undefined

    const today = startOfDay(new Date())
    const selectedDate = startOfDay(appointmentData.dateTime)

    if (selectedDate.getTime() === today.getTime()) {
      return getCurrentTime()
    }

    return undefined
  }

  const formData = !appointmentData.dateTime
    ? {
        appointmentType: appointmentData.appointmentType,
        date: '',
        time: '',
      }
    : {
        appointmentType: appointmentData.appointmentType,
        date: toDateSelectorFormat(appointmentData.dateTime),
        time: format(appointmentData.dateTime, 'HH:mm'),
      }

  const isFormLoading =
    isResourcesLoading ||
    isCustomerLoading ||
    isOpportunityLoading ||
    isAppointmentTypesLoading ||
    isAppointmentsLoading ||
    isIntegratedCalendarDataLoading

  if (isFormLoading) {
    return <CreateAppointmentNewSkeleton />
  }

  if (
    hasIntegratedCalendar &&
    finalCustomerGid &&
    finalCountryCode &&
    journey &&
    opportunityGid &&
    shopSalesforceId &&
    !isV2RedirectPending
  ) {
    const params = new URLSearchParams({
      action: 'schedule',
      utm_source: 'partner-portal',
      country: finalCountryCode.toUpperCase(),
      customer_gid: customerData?.salesforceId || finalCustomerGid,
      iframe: 'true',
      journey: journey,
      opportunity_gid: opportunityGid,
      shop_gid: shopSalesforceId,
      resource_id: resource?.id ?? '',
      appointment_type: AppointmentType.FOLLOW_UP,
      mode: 'partner-portal-neo',
    })

    return <Navigate to={`/${finalCountryCode.toUpperCase()}/consultant?${params.toString()}`} replace />
  }

  const hasRequiredParams = Boolean(customerData && finalCountryCode && opportunity && journey && opportunityGid)
  if (!hasRequiredParams) {
    return (
      <StyledContainer>
        <StyledTitle variant="h4" color="error">
          {t('createAppointment.missingParameters')}
        </StyledTitle>
        <Typography variant="body1" sx={{ mt: 2 }}>
          {!customerData && t('createAppointment.missingCustomer')}
          {!finalCountryCode && t('createAppointment.missingCountryCode')}
          {!opportunity && t('createAppointment.opportunityNotFound')}
          {opportunity && !journey && t('createAppointment.journeyNotFound')}
        </Typography>
      </StyledContainer>
    )
  }

  const handleSave = async () => {
    if (!customerData || !finalCountryCode || !journey || !opportunityGid) {
      setError(new Error('Missing required data'))
      return
    }

    if (!appointmentData.appointmentType || !appointmentData.dateTime) return

    const now = new Date()
    if (!isAfter(appointmentData.dateTime, now)) {
      setError(new Error('Cannot create appointment in the past'))
      return
    }

    const config = getAppointmentConfig(appointmentData.appointmentType)
    if (!config) {
      setError(new Error(`No configuration found for appointment type: ${appointmentData.appointmentType}`))
      return
    }

    const appointmentDataPayload: AppointmentCreateRequest = {
      country: finalCountryCode as Country,
      customer_salesforce_id: customerData.salesforceId,
      duration_in_minutes: config.duration_in_minutes,
      is_manual: false,
      journey: journey as Journey,
      opportunity_gid: opportunityGid,
      resource_ids: [],
      scheduled_customer_timezone: customerData.timezone || 'UTC',
      start_date: appointmentData.dateTime,
      title: `Appointment: w/${customerData.givenName} ${customerData.familyName}`,
      type: appointmentData.appointmentType as AppointmentType,
      shop_gid: opportunity.partnerShopSalesforceId,
    }

    createAppointmentMutation.mutate(appointmentDataPayload, {
      onSuccess: () => {
        setAppointmentData({
          appointmentType: undefined,
          dateTime: undefined,
        })
        setError(null)
        if (successPath) {
          navigate(successPath)
        }
      },
      onError: () => {
        setError(new Error('Error creating appointment'))
      },
    })
  }

  const isSaveEnabled = Boolean(
    appointmentData.appointmentType && appointmentData.dateTime && isAfter(appointmentData.dateTime, new Date())
  )

  return (
    <StyledContainer>
      <Stack spacing={3}>
        <AppointmentForm
          appointmentData={formData}
          availableAppointmentTypes={availableAppointmentTypes}
          onAppointmentTypeChange={handleAppointmentTypeChange}
          onDateChange={handleDateChange}
          onTimeChange={handleTimeChange}
          minDate={getTodayDate()}
          minTime={getMinTime()}
        />

        {error && (
          <Typography color="error" variant="body2" data-testid="appointment-create-error">
            {error.message}
          </Typography>
        )}

        <StyledActionButtonsContainer direction="row">
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={!isSaveEnabled || createAppointmentMutation.isLoading}
            data-testid="appointment-create-save-button"
            sx={{ position: 'fixed', bottom: 16, right: 16 }}
          >
            {createAppointmentMutation.isLoading ? t('common.label.saving') : t('common.label.save')}
          </Button>
        </StyledActionButtonsContainer>
      </Stack>
    </StyledContainer>
  )
}

export { CreateAppointmentNew }
